import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 py-12"
    >
      <Helmet>
        <title>Cookie Policy - Paradise-Performance</title>
        <meta name="description" content="Cookie policy explaining how we use cookies and similar technologies on our website." />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
          
          <div className="prose prose-gray max-w-none">
            <p className="text-sm text-gray-600 mb-6">Last Updated: January 1, 2024</p>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. What Are Cookies?</h2>
              <p className="text-gray-700 mb-4">
                Cookies are small text files that are placed on your device when you visit a website. They help websites remember your preferences, improve your user experience, and provide analytics about site usage.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Cookies</h2>
              <p className="text-gray-700 mb-4">Paradise-Performance uses cookies for the following purposes:</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Essential Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas of the website.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Functional Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies enable the website to provide enhanced functionality and personalization, such as remembering your preferences and choices.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Analytics Cookies</h3>
              <p className="text-gray-700 mb-4">
                We use analytics cookies to understand how visitors interact with our website, helping us improve our services and user experience.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Marketing Cookies</h3>
              <p className="text-gray-700 mb-4">
                These cookies track your online activity to help advertisers deliver more relevant advertising or to limit how many times you see an ad.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 mb-4">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cookie Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purpose</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">session_id</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Maintains user session</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">Session</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">user_preferences</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Stores user preferences</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">1 year</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">_ga, _gid</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Google Analytics</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">1 years</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">recently_viewed</td>
                      <td className="px-6 py-4 text-sm text-gray-700">Tracks recently viewed vehicles</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">30 days</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Third-Party Cookies</h2>
              <p className="text-gray-700 mb-4">
                We may allow third-party companies to use cookies on our website for the following purposes:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Google Analytics - Website traffic analysis</li>
                <li>Facebook Pixel - Social media marketing</li>
                <li>Google Ads - Advertising and remarketing</li>
                <li>YouTube - Video content delivery</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Managing Cookies</h2>
              <p className="text-gray-700 mb-4">
                You can control and manage cookies in various ways:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Browser Settings</h3>
              <p className="text-gray-700 mb-4">
                Most browsers allow you to view, delete, and block cookies from websites. Visit your browser's help pages for more information:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li><a href="https://support.google.com/chrome/answer/95647" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Edge</a></li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Opt-Out Links</h3>
              <p className="text-gray-700 mb-4">You can opt-out of third-party cookies:</p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li><a href="https://tools.google.com/dlpage/gaoptout" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-Out</a></li>
                <li><a href="https://www.facebook.com/help/568137493302217" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Facebook Ads Settings</a></li>
                <li><a href="https://adssettings.google.com" className="text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Ads Settings</a></li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Impact of Disabling Cookies</h2>
              <p className="text-gray-700 mb-4">
                Please note that disabling cookies may impact your experience on our website. Some features may not function properly, including:
              </p>
              <ul className="list-disc ml-6 mb-4 text-gray-700">
                <li>Inability to save preferences</li>
                <li>Difficulty completing forms</li>
                <li>Limited functionality in certain areas</li>
                <li>Less personalized experience</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Updates to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for legal reasons. We will notify you of any material changes by updating the date at the top of this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about our use of cookies, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg text-gray-700">
                <p className="font-semibold">Paradise-Performance</p>
                <p>802 S Church St</p>
                <p>Paradise, TX 76073</p>
                <p>Phone: (817) 382-2316</p>
                <p>Email: service@paradise-performance.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CookiePolicy;
